import { inject, Injectable, SecurityContext } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom, map } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';

export interface Plan {
  id: string;
  title: string;
  type: any;
  price: number;
  validity: any;
  max_accounts: number | null;
  max_sessions: number;
  discount_sessions: number;
  max_events: number;
  discount_events: number;
  provider_discount: any;
  image: string | null;
  detail_html: any;
  yearly_price: number | null;
  summary: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class PlansService {
  private apiUrl = environment.apiUrl;
  private httpClient = inject(HttpClient);
  private sanitizer = inject(DomSanitizer);

  private headers = new HttpHeaders({
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0',
  });

  async getRecentPlansFromApi() {
    const timestamp = new Date().getTime();
    return firstValueFrom(
      this.httpClient.get(`${this.apiUrl}/subscriptions?type=individual&paginate=disabled&t=${timestamp}`, { headers: this.headers })
      .pipe(
        map((response: any) => {
          const plans = response.data;
          // const totalPages = response.meta.last_page;
          // const currentPage = response.meta.current_page;
          // const total = response.meta.total;

          return {
            data: plans.map((plan: any) => ({
              ...plan,
              currency: 'Bs.',
              recommended: plan.important,
              features: [
                // `<b>${plan.max_sessions}</b> sesion${plan.max_sessions > 1 ? 'es' : ''} por cuenta`,
                // `<b>${plan.discount_sessions}%</b> de descuento en sesion${plan.discount_sessions > 1 ? 'es' : ''}`,
                // `Acceso a <b>${plan.max_events}</b> evento${plan.max_events > 1 ? 's' : ''}`,
                // `<b>${plan.discount_events}%</b> de descuento en eventos`,
                // ...(plan?.provider_discount?.label
                //   ? [
                //       `<b>${plan.provider_discount.label}</b> de descuento con nuestros proveedores aliados`,
                //     ]
                //   : []),
                ...this.parseMoreBenefits(plan.detail_html?.content),
              ],
              // year features
              year_features: [
                // `<b>${plan.max_sessions}</b> sesion${plan.max_sessions > 1 ? 'es' : ''} por cuenta (por mes)`,
                // `<b>${plan.discount_sessions}%</b> de descuento en sesion${plan.discount_sessions > 1 ? 'es' : ''}`,
                // `Acceso a <b>${plan.max_events}</b> evento${plan.max_events > 1 ? 's' : ''} (por mes)`,
                // `<b>${plan.discount_events}%</b> de descuento en eventos`,
                // ...(plan?.provider_discount?.label
                //   ? [
                //       `<b>${plan.provider_discount.label}</b> de descuento con nuestros proveedores aliados`,
                //     ]
                //   : []),
                ...this.parseMoreBenefits(plan.detail_html?.content),
              ],

              // description: `<p>Obten un <span class="${plan.important ? 'text-secondary' : 'text-heading-primary'} font-semibold">${plan.provider_discount.label}</span> de descuento en tu suscripción</p>`,
              description: this.parseDescription(plan.summary, plan.important),

              link: `/plans/${plan.id}`,
            })),
            // totalPages,
            // currentPage,
            // total
          };
        })
    ));
  }

  async getPlanById(id: string) {
    return firstValueFrom(
      this.httpClient.get(`${this.apiUrl}/subscriptions/${id}`, { headers: this.headers })
      .pipe(
        map((response: any) => {
          return response.data;
        })
      )
    );
  }

  private parseMoreBenefits(detail: string) {
    if (!detail) return [];
    const sanitizedDetail = this.sanitizer.sanitize(SecurityContext.HTML, detail.trim());
   
    // remove <ul> or <ol> or <p> and </ul> or </ol> or </p>
    const regex = /<ul>|<ol>|<p>|<\/ul>|<\/ol>|<\/p>/g;

    // first replace <p></p> with <li></li>
    const regexP = /<p>(.*?)<\/p>/g;
    let result = sanitizedDetail?.replace(regexP, '<li>$1</li>');


    result = result?.replace(regex, '');

    // split by <li> and remove </li>
    const liArray = result?.split('<li>');
    const liArray2 = liArray?.map((li: string) => li.replace('</li>', ''));

    const resp = liArray2?.map((li: string) => {
      const trimmedLi = li.trim();
      // if (trimmedLi.length > 500) {
      //   let truncated = trimmedLi.slice(0, 500);
      //   const lastSpace = truncated.lastIndexOf(' ');
      //   if (lastSpace > 0) {
      //     truncated = truncated.slice(0, lastSpace).trim();
      //   }
      //   return truncated + '...';
      // }
      return trimmedLi;
    }) || [];

    return resp.filter((li: string) => li.length > 0);
  }

  private parseDescription(summary: string, isRecommended: boolean) {
    if (!summary) return '';

    // find number with percentage and add span with class text-secondary
    const regex = /\d+%/g;
    const result = summary?.replace(regex, (match) => `<b class="${isRecommended ? 'text-secondary' : 'text-heading-primary'} font-semibold">${match}</b>`);

    const sanitizedSummary = this.sanitizer.sanitize(SecurityContext.HTML, result.trim());
    return sanitizedSummary;
  }

  async getBankQRData() {
    const timestamp = new Date().getTime();
    const headers = this.headers;

    const [bankResponse, qrResponse] = await Promise.all([
      firstValueFrom(this.httpClient.get(`${this.apiUrl}/extra-datas?key=subscription_bank&t=${timestamp}`, { headers })),
      firstValueFrom(this.httpClient.get(`${this.apiUrl}/extra-datas?key=subscription_qr&t=${timestamp}`, { headers }))
    ]);

    return {
      data: {
        value: (bankResponse as any).data.value,
        file: (qrResponse as any).data.file,
      }
    };
  }
}
